import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { TransformationModes } from '../components/TransformationModes';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, ScrollToTop, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground, SoloAnnualPromoCard } from '../components/shared';
import { useSoloAnnualPromo } from '../hooks/useSoloAnnualPromo';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';
import ObjectivesSection from '../components/ObjectivesSection';
import MethodSection from '../components/MethodSection';

const HomePage = () => {
  const { t, language } = useLanguage();
  const [showAllFaq, setShowAllFaq] = useState(false);
  const promo = useSoloAnnualPromo();
  
  return (
    <main id="main-content" role="main" data-testid="home-page">
      <SEOHead
        title="Body-Up Personal Training Nyon | Coach Sportif Privé | Studio 150m²"
        description="Studio privé de personal training à Nyon depuis 2011. Coaching 100% personnalisé, EMS Drysuit XBody, Scanner 3D Styku, Plan nutrition. Perte de poids, transformation musculaire, remise en forme. Consultation GRATUITE. Clients de Gland, Rolle, Coppet, Genève."
        path="/"
      />
      <section className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
        {/* Top section - Black background */}
        <div
          className="bg-black pb-3 sm:pb-4 px-4 sm:px-6 lg:px-8 text-center relative z-20"
          style={{ paddingTop: 'calc(var(--app-header-h, 5rem) + 0.75rem)' }}
        >
          <AnimatedSection>
            <SectionBadge emoji="🏆" compact className="hero-badge-desktop">{t.hero.badge}</SectionBadge>
          </AnimatedSection>
          
          {/* Single CTA Button - Book Now */}
          <div className="flex flex-col items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-3">
            <AnimatedSection delay={150}>
              <a 
                href={BOOKING_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="group inline-block"
                data-testid="book-now-btn"
              >
                <div className="relative inline-flex items-center gap-1.5 sm:gap-2 px-5 sm:px-7 py-2.5 sm:py-2.5 bg-gradient-to-r from-primary via-primary to-pink-500 rounded-full text-white font-bold text-xs sm:text-sm group-hover:scale-105 transition-all duration-500 border border-primary/30 shadow-lg shadow-primary/25 group-hover:shadow-primary/40 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                  <Calendar className="w-4 sm:w-4.5 h-4 sm:h-4.5 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] relative z-10" />
                  <span className="tracking-wide relative z-10">{language === 'fr' ? 'Réserver maintenant' : 'Book now'}</span>
                  <ArrowRight className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-white/80 group-hover:text-white group-hover:translate-x-1 transition-all duration-300 relative z-10" />
                </div>
              </a>
            </AnimatedSection>
            <p className="text-[10px] sm:text-xs text-zinc-600 mt-0.5">{language === 'fr' ? 'Consultation gratuite sur rendez-vous' : 'Free consultation by appointment'}</p>
          </div>
        </div>

        {/* Middle section with image and main content */}
        <div className="flex-1 relative">
          {/* Background Image */}
          <div className="absolute inset-0">
            <div 
              className="absolute inset-0 bg-no-repeat hero-bg-mobile"
              style={{ 
                backgroundImage: `url(${STUDIO_IMAGE})`
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/40 to-black" />
            <ParticlesBackground />
          </div>

          {/* Title and subtitle - Centered on image */}
          <div className="relative z-10 flex items-center sm:items-start justify-center h-full py-8 sm:pt-8 lg:pt-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <AnimatedSection delay={100}>
                <h1 className="text-3xl md:text-6xl lg:text-7xl font-bold text-white mb-3 sm:mb-6 leading-tight">
                  {t.hero.title1}<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400 animate-gradient">{t.hero.title2}</span>
                </h1>
              </AnimatedSection>

              <AnimatedSection delay={200}>
                <p className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
                  {t.hero.subtitle}
                </p>
              </AnimatedSection>

              {/* Format pills — compréhension immédiate de l'offre (présentiel · en ligne · hybride · outils) */}
              <AnimatedSection delay={280}>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 mt-5 sm:mt-7" data-testid="hero-format-pills">
                  {[
                    { icon: Dumbbell, label: language === 'fr' ? 'Présentiel Nyon' : 'In-studio Nyon', to: '/services', testId: 'hero-pill-presentiel' },
                    { icon: Monitor, label: language === 'fr' ? '100% en ligne' : '100% online', to: '/parcours/coaching-online', testId: 'hero-pill-online' },
                    { icon: Zap, label: language === 'fr' ? 'Hybride' : 'Hybrid', to: '/parcours/coaching-hybride', testId: 'hero-pill-hybride' },
                    { icon: Apple, label: language === 'fr' ? 'Calculateur calories' : 'Calorie calculator', to: '/calculateur-calories', testId: 'hero-pill-calculator' },
                    { icon: Activity, label: language === 'fr' ? 'App de suivi' : 'Tracking app', to: '/offres?cat=coaching', testId: 'hero-pill-app' },
                  ].map((p) => (
                    <Link
                      key={p.testId}
                      to={p.to}
                      data-testid={p.testId}
                      className="group inline-flex items-center gap-1.5 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full bg-white/8 backdrop-blur-sm border border-white/15 text-white/90 text-xs sm:text-sm font-medium hover:bg-primary/20 hover:border-primary/50 hover:text-white transition-all duration-300"
                    >
                      <p.icon className="w-3.5 h-3.5 text-primary group-hover:scale-110 transition-transform" />
                      {p.label}
                    </Link>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>

        {/* Bottom section — Badge WAHOU vers les prestations présentielles */}
        <div className="bg-black py-10 px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection delay={300}>
              <div className="flex justify-center">
                <Link
                  to="/services"
                  data-testid="hero-services-badge"
                  aria-label={language === 'fr' ? 'Découvrir toutes mes prestations en présentiel à Nyon' : 'Discover all my in-studio services in Nyon'}
                  className="group relative"
                >
                  {/* Glow animé derrière */}
                  <span className="absolute -inset-4 rounded-full bg-gradient-to-r from-primary via-purple-500 to-pink-500 opacity-40 blur-2xl group-hover:opacity-70 animate-pulse transition-opacity duration-500" aria-hidden="true" />
                  <span className="relative inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 pl-2 pr-4 sm:pr-5 py-2 rounded-full bg-gradient-to-r from-primary/95 via-purple-600/95 to-pink-500/95 border border-white/25 shadow-2xl shadow-primary/40 hover:scale-[1.04] transition-transform duration-300 overflow-hidden max-w-[92vw] sm:max-w-none">
                    {/* Shimmer */}
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/40 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" aria-hidden="true" />
                    <span className="relative z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[10px] sm:text-[11px] font-black tracking-widest text-primary whitespace-nowrap">
                      <Dumbbell className="w-3.5 h-3.5" /> {language === 'fr' ? 'PRÉSENTIEL · NYON' : 'IN-STUDIO · NYON'}
                    </span>
                    <span className="relative z-10 text-sm sm:text-base font-bold text-white text-center sm:whitespace-nowrap">
                      {language === 'fr' ? 'Découvre toutes mes prestations sur place' : 'Discover all my in-studio services'}
                    </span>
                    <ArrowRight className="relative z-10 w-4 h-4 sm:w-4.5 sm:h-4.5 text-white group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </div>
            </AnimatedSection>

            {/* Micro-features simples (kept, sans "Consultation gratuite") */}
            <AnimatedSection delay={400}>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-white/70 mt-6">
                {[
                  { icon: Check, text: t.hero.features.private },
                  { icon: Check, text: t.hero.features.experience },
                  { icon: Check, text: t.hero.features.price }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 hover:text-primary transition-colors">
                    <item.icon className="w-4 h-4 text-primary" />
                    <span className="text-xs sm:text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 🎯 3 façons de te coacher — Présentiel · En ligne · Hybride · App */}
      <TransformationModes />

      {/* Trust Stats Bar */}
      <section className="bg-black border-y border-white/5 py-8 sm:py-10" data-testid="trust-stats">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 text-center">
            {[
              { value: language === 'fr' ? 'Depuis 2005' : 'Since 2005', label: language === 'fr' ? "d'expérience" : 'of experience', icon: Award },
              { value: '150m²', label: language === 'fr' ? 'studio privé' : 'private studio', icon: Home },
              { value: '4.9/5', label: language === 'fr' ? 'avis clients' : 'client reviews', icon: Star },
              { value: '100%', label: language === 'fr' ? 'personnalisé' : 'personalized', icon: Target },
            ].map((stat, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="group">
                  <stat.icon className="w-5 h-5 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-xl sm:text-2xl font-black text-white">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-zinc-500 mt-1">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 🔥 CTA Calculateur gratuit — motive à essayer l'app immédiatement */}
      <section className="py-6 bg-black" data-testid="free-calculator-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/calculateur-calories"
            data-testid="free-calculator-cta-link"
            className="group relative flex flex-col sm:flex-row items-center justify-between gap-4 overflow-hidden rounded-2xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 via-primary/10 to-transparent px-6 py-5 hover:border-yellow-500/50 transition-colors duration-300"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" aria-hidden="true" />
            <div className="relative z-10 flex items-center gap-3 text-center sm:text-left">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/15 flex items-center justify-center shrink-0">
                <Apple className="w-5 h-5 text-yellow-400" />
              </div>
              <p className="text-white font-semibold text-sm sm:text-base">
                {language === 'fr' ? 'Testez notre calculateur de calories gratuitement en cliquant ici' : 'Try our free calorie calculator by clicking here'}
              </p>
            </div>
            <span className="relative z-10 shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-black bg-gradient-to-r from-yellow-400 to-primary group-hover:scale-[1.03] transition-transform">
              {language === 'fr' ? "C'est gratuit" : "It's free"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>
      </section>

      {/* 🔥 Promo -50% Suivi Pro+ annuel (wahou, auto-expire le 31 octobre) */}
      {promo?.active && (
        <section className="py-2 bg-black" data-testid="home-solo-promo-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SoloAnnualPromoCard promo={promo} language={language} ctaHref="/offres" compact />
          </div>
        </section>
      )}

      {/* 🎯 Choisissez votre parcours — 3 objectifs clients */}
      <ObjectivesSection language={language} />

      {/* Benefits Section — Pourquoi Body-Up (design premium refondu) */}
      <section className="relative py-20 sm:py-24 bg-gradient-to-b from-black via-zinc-950 to-black overflow-hidden" data-testid="benefits-section">
        {/* Ambient glows */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute top-1/4 -left-32 w-[30rem] h-[30rem] bg-primary/15 rounded-full blur-[140px]" />
          <div className="absolute bottom-1/4 -right-32 w-[30rem] h-[30rem] bg-purple-500/12 rounded-full blur-[140px]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.7) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <AnimatedSection>
            <div className="text-center mb-14 sm:mb-16">
              <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-widest bg-white/5 border border-white/10 text-zinc-300 mb-4">
                {language === 'fr' ? '✨ POURQUOI BODY-UP' : '✨ WHY BODY-UP'}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {language === 'fr' ? 'L\'excellence,' : 'Excellence,'}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-purple-400">
                  {language === 'fr' ? 'sans compromis' : 'no compromise'}
                </span>
              </h2>
              <p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
                {t.benefits.description}
              </p>
            </div>
          </AnimatedSection>

          {/* 6 cards en 2 lignes de 3 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {[
              { icon: Shield, title: t.benefits.items[0].title, description: t.benefits.items[0].description, accent: 'from-primary to-pink-500', ring: 'shadow-primary/20', text: 'text-primary' },
              { icon: Target, title: t.benefits.items[1].title, description: t.benefits.items[1].description, accent: 'from-pink-500 to-purple-500', ring: 'shadow-pink-500/20', text: 'text-pink-400' },
              { icon: Activity, title: t.benefits.items[2].title, description: t.benefits.items[2].description, accent: 'from-blue-500 to-cyan-500', ring: 'shadow-blue-500/20', text: 'text-blue-400' },
              { icon: Award, title: t.benefits.items[3].title, description: t.benefits.items[3].description, accent: 'from-amber-500 to-orange-500', ring: 'shadow-amber-500/20', text: 'text-amber-400' },
              { icon: Zap, title: t.benefits.items[4].title, description: t.benefits.items[4].description, accent: 'from-purple-500 to-fuchsia-500', ring: 'shadow-purple-500/20', text: 'text-purple-400' },
              { icon: Heart, title: t.benefits.items[5].title, description: t.benefits.items[5].description, accent: 'from-red-500 to-rose-500', ring: 'shadow-red-500/20', text: 'text-red-400' },
            ].map((b, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <div className={`group relative h-full overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border border-white/8 p-6 shadow-2xl ${b.ring} hover:-translate-y-1.5 transition-all duration-300`}>
                  {/* Corner glow */}
                  <div className={`absolute -top-14 -right-14 w-32 h-32 rounded-full bg-gradient-to-br ${b.accent} opacity-15 blur-3xl group-hover:opacity-30 transition-opacity`} aria-hidden="true" />
                  {/* Icon */}
                  <div className={`relative w-12 h-12 rounded-2xl bg-gradient-to-br ${b.accent} flex items-center justify-center shadow-lg ${b.ring} mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <b.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className={`text-lg sm:text-xl font-bold text-white leading-tight ${b.text} transition-colors`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {b.title}
                  </h3>
                  <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
                    {b.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* La Méthode Body-Up en 4 étapes — remplace l'ancien duplicate calculator CTA */}
      <MethodSection language={language} />

      {/* EMS Section - BLACK BACKGROUND */}
      <section className="py-20 bg-gradient-to-r from-primary/20 via-black to-black relative overflow-hidden" data-testid="ems-section">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: "radial-gradient(circle, rgba(185,32,116,0.3) 1px, transparent 1px)", backgroundSize: "20px 20px"}} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div>
                <SectionBadge emoji="⚡">{language === 'fr' ? 'Nouveau : Body-Up EMS Training' : 'New: Body-Up EMS Training'}</SectionBadge>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                  {language === 'fr' ? <>Découvrez l&apos;EMS<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-500">en Petits Groupes</span></> : <>Discover EMS<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-500">in Small Groups</span></>}
                </h2>
                <p className="text-gray-300 text-lg mb-8">
                  {language === 'fr'
                    ? "Body-Up EMS Training est notre extension dédiée à l'électrostimulation musculaire. Des séances courtes (20-25 min) en petits groupes (max 4 personnes) avec la technologie XBody sans fil — idéales pour les agendas chargés."
                    : 'Body-Up EMS Training is our extension dedicated to electro-muscle stimulation. Short sessions (20-25 min) in small groups (max 4 people) with wireless XBody technology — perfect for busy schedules.'}
                </p>

                <div className="space-y-4 mb-8">
                  {(language === 'fr'
                    ? ["Body-Up = Coaching privé 1:1 en studio", "Body-Up EMS = Cours EMS en petits groupes", "Même équipe, même qualité, même passion"]
                    : ['Body-Up = Private 1:1 coaching in studio', 'Body-Up EMS = EMS classes in small groups', 'Same team, same quality, same passion']
                  ).map((item, i) => (
                    <div key={i} className="flex items-center gap-3 group">
                      <CheckCircle2 className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" />
                      <span className="text-white">{item}</span>
                    </div>
                  ))}
                </div>

                <a href={EMS_SITE_URL} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all hover:scale-105 group" data-testid="discover-ems-btn">
                    {t.emsSection.cta}
                    <ExternalLink className="w-5 h-5 ml-2 group-hover:rotate-12 transition-transform" />
                  </Button>
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="relative">
                <div className="bg-gradient-to-br from-primary/30 to-primary/10 rounded-3xl p-8 border border-primary/30 hover:border-primary/50 transition-all">
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { value: "25", label: t.emsSection.stats.minutes },
                      { value: "4", label: t.emsSection.stats.people },
                      { value: "90%", label: t.emsSection.stats.muscles },
                      { value: "1-2x", label: t.emsSection.stats.frequency }
                    ].map((stat, i) => (
                      <div key={i} className="text-center group hover:scale-105 transition-transform">
                        <div className="text-4xl font-bold text-primary">{stat.value}</div>
                        <div className="text-white text-sm">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Testimonials Section - BLACK BACKGROUND */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden" data-testid="testimonials-section">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl opacity-30" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              {/* Google Badge */}
              <div className="inline-flex items-center gap-4 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6">
                <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">4.9</span>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-white font-medium">{language === 'fr' ? '100% Vérifié' : '100% Verified'}</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-2">
                {language === 'fr' ? <>Ce que disent <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">nos clients</span></> : <>What our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">clients say</span></>}
              </h2>
              <p className="text-gray-400 text-lg">
                {language === 'fr' ? 'Découvrez les témoignages authentiques de nos membres' : 'Discover authentic testimonials from our members'}
              </p>
            </div>
          </AnimatedSection>

          {/* Testimonials Carousel */}
          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* FAQ Preview - BLACK BACKGROUND with WAHOU effect */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden" data-testid="faq-preview">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl opacity-30" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <SectionBadge emoji="❓">{t.faq.subtitle}</SectionBadge>
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">{t.faq.title}</span>
              </h2>
              <p className="text-gray-400 mt-4">
                {language === 'fr' ? "Tout ce que vous devez savoir avant de commencer 🎯" : "Everything you need to know before starting 🎯"}
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="grid gap-4">
              {faqItems.slice(0, showAllFaq ? 6 : 2).map((item, index) => (
                <div
                  key={index}
                  className="group"
                >
                  <Accordion type="single" collapsible>
                    <AccordionItem value={`item-${index}`} className="border-0">
                      <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/20 hover:border-primary/50 transition-all duration-500 hover:shadow-lg hover:shadow-primary/10 overflow-hidden">
                        <AccordionTrigger className="text-white text-left hover:text-primary px-4 sm:px-6 py-4 sm:py-5 hover:no-underline w-full">
                          <span className="flex items-center gap-3 sm:gap-4 w-full">
                            <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-pink-500 rounded-xl sm:rounded-2xl text-xl sm:text-2xl shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform">
                              {item.emoji}
                            </span>
                            <span className="font-semibold text-base sm:text-lg flex-1 text-left">{language === 'fr' ? item.question : item.questionEn}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                          <div className="bg-gradient-to-br from-primary/15 to-primary/5 rounded-xl p-4 border border-primary/20">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {(language === 'fr' ? item.answerPoints : item.answerPointsEn).map((point, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm sm:text-base animate-fadeInUp" style={{ animationDelay: `${i * 100}ms` }}>
                                  <span className={`font-bold ${i % 2 === 0 ? 'text-primary' : 'text-white'}`}>{point}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </AccordionContent>
                      </div>
                    </AccordionItem>
                  </Accordion>
                </div>
              ))}
            </div>

            {/* Show more button */}
            {!showAllFaq && faqItems.length > 2 && (
              <div className="mt-6 text-center">
                <button onClick={() => setShowAllFaq(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/15 rounded-xl text-gray-300 text-sm font-medium hover:border-primary/40 hover:text-white transition-all duration-300"
                  data-testid="faq-show-more">
                  <ChevronDown className="w-4 h-4" />
                  {language === 'fr' ? `Voir ${faqItems.length - 2} questions de plus` : `Show ${faqItems.length - 2} more questions`}
                </button>
              </div>
            )}
          </AnimatedSection>

          {/* CTA after FAQ */}
          <AnimatedSection delay={200}>
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-4 px-6 py-4 bg-gradient-to-r from-primary/20 to-pink-500/20 rounded-2xl border border-primary/30">
                <span className="text-white">
                  {language === 'fr' ? "🤔 Encore des questions ?" : "🤔 Still have questions?"}
                </span>
                <Link to="/faq">
                  <Button className="bg-primary hover:bg-primary/90 text-white">
                    {language === 'fr' ? "Voir toutes les FAQ" : "See all FAQs"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section - Bottom of Homepage */}
      <HomeContactSection />
    </main>
  );
};

// About Page

export default HomePage;
